import React from 'react';
import { useTranslation } from 'react-i18next';

interface PrivacyPolicyProps {
  onNavigate: (page: string) => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-sm rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('privacyPolicy.title')}</h1>
          
          <div className="prose max-w-none">
            <p className="text-lg text-gray-600 mb-6">
              {t('privacyPolicy.lastUpdated', { date: new Date().toLocaleDateString() })}
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('privacyPolicy.sections.introduction.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('privacyPolicy.sections.introduction.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('privacyPolicy.sections.informationWeCollect.title')}</h2>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('privacyPolicy.sections.informationWeCollect.personalInformation.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('privacyPolicy.sections.informationWeCollect.personalInformation.content')}
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                {(t('privacyPolicy.sections.informationWeCollect.personalInformation.whenList', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              
              <p className="text-gray-700 mb-4">{t('privacyPolicy.sections.informationWeCollect.personalInformation.mayInclude')}</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                {(t('privacyPolicy.sections.informationWeCollect.personalInformation.includesList', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('privacyPolicy.sections.informationWeCollect.automaticallyCollected.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('privacyPolicy.sections.informationWeCollect.automaticallyCollected.content')}
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-green-900 mb-2">{t('privacyPolicy.sections.informationWeCollect.ga4.title')}</h4>
                <p className="text-sm text-green-800 mb-2">
                  {t('privacyPolicy.sections.informationWeCollect.ga4.description')}
                </p>
                <ul className="text-sm text-green-800 space-y-1">
                  {(t('privacyPolicy.sections.informationWeCollect.ga4.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                    <li key={index}>• {feature}</li>
                  ))}
                </ul>
              </div>

              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('privacyPolicy.sections.informationWeCollect.informationWeDoNotCollect.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('privacyPolicy.sections.informationWeCollect.informationWeDoNotCollect.intro')}
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                {(t('privacyPolicy.sections.informationWeCollect.informationWeDoNotCollect.list', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('privacyPolicy.sections.howWeUse.title')}</h2>
              <p className="text-gray-700 mb-4">{t('privacyPolicy.sections.howWeUse.content')}</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                {(t('privacyPolicy.sections.howWeUse.list', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('privacyPolicy.sections.informationSharing.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('privacyPolicy.sections.informationSharing.content')}
              </p>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('privacyPolicy.sections.informationSharing.serviceProviders.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('privacyPolicy.sections.informationSharing.serviceProviders.content')}
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                {(t('privacyPolicy.sections.informationSharing.serviceProviders.list', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('privacyPolicy.sections.informationSharing.legalRequirements.title')}</h3>
              <p className="text-gray-700 mb-4">
                {t('privacyPolicy.sections.informationSharing.legalRequirements.content')}
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                {(t('privacyPolicy.sections.informationSharing.legalRequirements.list', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('privacyPolicy.sections.dataSecurity.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('privacyPolicy.sections.dataSecurity.content')}
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                {(t('privacyPolicy.sections.dataSecurity.list', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('privacyPolicy.sections.yourRights.title')}</h2>
              <p className="text-gray-700 mb-4">{t('privacyPolicy.sections.yourRights.content')}</p>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('privacyPolicy.sections.yourRights.accessPortability.title')}</h3>
              <p className="text-gray-700 mb-4">{t('privacyPolicy.sections.yourRights.accessPortability.content')}</p>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('privacyPolicy.sections.yourRights.correction.title')}</h3>
              <p className="text-gray-700 mb-4">{t('privacyPolicy.sections.yourRights.correction.content')}</p>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('privacyPolicy.sections.yourRights.accountDeletion.title')}</h3>
              <p className="text-gray-700 mb-4">{t('privacyPolicy.sections.yourRights.accountDeletion.content')}</p>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('privacyPolicy.sections.yourRights.restriction.title')}</h3>
              <p className="text-gray-700 mb-4">{t('privacyPolicy.sections.yourRights.restriction.content')}</p>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">{t('privacyPolicy.sections.yourRights.objection.title')}</h3>
              <p className="text-gray-700 mb-4">{t('privacyPolicy.sections.yourRights.objection.content')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('privacyPolicy.sections.dataRetention.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('privacyPolicy.sections.dataRetention.content')}
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                {(t('privacyPolicy.sections.dataRetention.list', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('privacyPolicy.sections.changesToPolicy.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('privacyPolicy.sections.changesToPolicy.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('privacyPolicy.sections.contactUs.title')}</h2>
              <p className="text-gray-700 mb-4">
                {t('privacyPolicy.sections.contactUs.content')}
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                {(t('privacyPolicy.sections.contactUs.methods', { returnObjects: true }) as string[]).map((method: string, index: number) => (
                  <li key={index}>{method}</li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;